import { useState, useMemo, useEffect, forwardRef } from "react";

import { motion } from "framer-motion";
import { GitCompare, Trophy, ArrowRight, ArrowLeftRight, Info, RefreshCw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { allProducts, type Product, type ProductCategory } from "@/data/productDatabase";
import { compareCategoryOptions } from "@/data/compareCategories";
import ProductSearchCombobox from "@/components/ProductSearchCombobox";
import ProductThumbnail from "@/components/products/ProductThumbnail";
import RatingMethodologyCallout from "@/components/products/RatingMethodologyCallout";
import { computeRatings, fallbackUserReviews, type ComputedRating, type UserReviewsData, type ProductRatings } from "@/lib/ratingsEngine";
import { getCachedReviews, refreshReviews, refreshReviewsIfMissing } from "@/lib/productReviews";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

// ── Tooltip body: computed factor breakdown ────────────────────────
const ComputedBreakdown = ({ rating }: { rating?: ComputedRating }) => {
  if (!rating) return <p>No breakdown available.</p>;
  return (
    <div className="space-y-3 min-w-[280px] max-w-[320px]">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-border">
        <p className="text-base font-display font-bold text-foreground">
          Score: <span className="text-primary">{rating.score}</span>
          <span className="text-muted-foreground text-xs">/100</span>
        </p>
      </div>
      <p className="text-xs text-foreground/85 leading-relaxed">{rating.summary}</p>
      <div className="pt-2 border-t border-border space-y-2">
        <p className="text-[10px] font-mono text-accent uppercase tracking-wider font-bold">
          How we got to {rating.score}
        </p>
        <div className="flex items-start justify-between gap-3 text-xs">
          <span className="flex-1 text-muted-foreground italic">Industry-average starting point</span>
          <span className="font-mono font-bold text-muted-foreground shrink-0">{rating.baseline}</span>
        </div>
        <ul className="space-y-2">
          {rating.factors.map((f, i) => (
            <li key={i} className="space-y-0.5">
              <div className="flex items-start justify-between gap-3 text-xs">
                <span className="flex-1 text-foreground/95 font-medium">{f.label}</span>
                <span
                  className={`font-mono font-bold shrink-0 px-1.5 py-0.5 rounded text-[11px] ${
                    f.delta >= 0 ? "text-primary bg-primary/10" : "text-destructive bg-destructive/10"
                  }`}
                >
                  {f.delta >= 0 ? "+" : ""}
                  {f.delta} pts
                </span>
              </div>
              {f.detail && (
                <p className="text-[10.5px] text-muted-foreground leading-snug pl-0.5">{f.detail}</p>
              )}
            </li>
          ))}
        </ul>
        <div className="flex items-start justify-between gap-3 text-xs pt-1.5 border-t border-border/60">
          <span className="flex-1 font-bold text-foreground">Final score</span>
          <span className="font-mono font-bold text-primary shrink-0">{rating.score}/100</span>
        </div>
      </div>
    </div>
  );
};

// ── Tooltip body: live user reviews + refresh ──────────────────────
const ReviewsBreakdown = ({
  data,
  refreshing,
  onRefresh,
}: {
  data?: UserReviewsData;
  refreshing: boolean;
  onRefresh: () => void;
}) => {
  if (!data) return <p className="text-sm">No review data.</p>;
  return (
    <div className="space-y-2.5 min-w-[260px]">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-border">
        <p className="text-base font-display font-bold text-foreground">
          <span className="text-primary">{data.averageRating.toFixed(1)}★</span> · {data.reviewCount} reviews
        </p>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold border ${
            data.confidence === "high"
              ? "bg-primary/20 text-primary border-primary/40"
              : data.confidence === "medium"
              ? "bg-accent/20 text-accent border-accent/40"
              : "bg-muted text-muted-foreground border-border"
          }`}
        >
          {data.confidence} conf
        </span>
      </div>
      {data.summary && <p className="text-sm text-foreground/80 italic leading-relaxed">"{data.summary}"</p>}
      {data.sources.length > 0 && (
        <ul className="space-y-1 pt-2 border-t border-border">
          {data.sources.map((s, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline flex items-center gap-1 truncate font-medium"
              >
                {s.name} <ExternalLink className="w-3 h-3" />
              </a>
              {s.rating !== undefined && <span className="text-foreground/70 ml-auto font-mono">{s.rating}★</span>}
              {s.count !== undefined && <span className="text-muted-foreground font-mono">({s.count})</span>}
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="w-full mt-1 flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-bold text-foreground hover:text-primary bg-secondary hover:bg-primary/10 border border-border hover:border-primary/40 rounded-md px-3 py-2 transition-all disabled:opacity-50"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        {refreshing ? "Pulling fresh data…" : "Refresh from web"}
      </button>
      {data.lastRefreshed && (
        <p className="text-[10px] text-muted-foreground text-center font-mono">
          Updated {new Date(data.lastRefreshed).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

// ── Rating row ─────────────────────────────────────────────────────
interface RatingRowProps {
  label: string;
  valueA: number;
  valueB: number;
}

const RatingBar = forwardRef<HTMLDivElement, RatingRowProps>(({ label, valueA, valueB }, ref) => {
  const aWins = valueA > valueB;
  const bWins = valueB > valueA;

  return (
    <div ref={ref} className="space-y-2">
      <p className="text-base font-display uppercase tracking-wider text-center text-primary sm:text-xl font-extrabold">
        {label}
      </p>
      <div className="flex items-center gap-3">
        <span
          className={`text-sm sm:text-base font-mono font-bold w-12 text-right ${
            aWins ? "text-primary" : "text-foreground"
          }`}
        >
          {valueA}%
        </span>
        <div className="flex-1 flex gap-1 h-3.5">
          <div className="flex-1 bg-surface-sunken rounded-l-full overflow-hidden flex justify-end">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${valueA}%` }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={`h-full rounded-l-full ${
                aWins
                  ? "bg-gradient-to-r from-primary/60 to-primary shadow-[0_0_6px_hsl(var(--primary)/0.3)]"
                  : "bg-gradient-to-r from-muted-foreground/30 to-muted-foreground/50"
              }`}
            />
          </div>
          <div className="flex-1 bg-surface-sunken rounded-r-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${valueB}%` }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={`h-full rounded-r-full ${
                bWins
                  ? "bg-gradient-to-l from-accent/60 to-accent shadow-[0_0_6px_hsl(var(--accent)/0.3)]"
                  : "bg-gradient-to-l from-muted-foreground/30 to-muted-foreground/50"
              }`}
            />
          </div>
        </div>
        <span
          className={`text-sm sm:text-base font-mono font-bold w-12 ${
            bWins ? "text-accent" : "text-foreground"
          }`}
        >
          {valueB}%
        </span>
      </div>
    </div>
  );
});
RatingBar.displayName = "RatingBar";

const ComparisonSection = () => {
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory>("subwoofer");
  const [productAId, setProductAId] = useState("");
  const [productBId, setProductBId] = useState("");

  // Live review state keyed by product id
  const [reviews, setReviews] = useState<Record<string, UserReviewsData>>({});
  const [refreshing, setRefreshing] = useState<Record<string, boolean>>({});

  // Filter products by selected category
  const categoryProducts = useMemo(
    () => allProducts.filter((p) => p.category === selectedCategory),
    [selectedCategory]
  );

  // Auto-select first two when category changes
  const effectiveAId = productAId && categoryProducts.some(p => p.id === productAId) ? productAId : categoryProducts[0]?.id ?? "";
  const effectiveBId = productBId && categoryProducts.some(p => p.id === productBId) && productBId !== effectiveAId ? productBId : categoryProducts[1]?.id ?? "";

  const productA = categoryProducts.find((p) => p.id === effectiveAId);
  const productB = categoryProducts.find((p) => p.id === effectiveBId);

  // Compute deterministic ratings for current pair
  const computedA = useMemo<ProductRatings | undefined>(
    () => (productA ? computeRatings(productA) : undefined),
    [productA],
  );
  const computedB = useMemo<ProductRatings | undefined>(
    () => (productB ? computeRatings(productB) : undefined),
    [productB],
  );

  // Seed fallback review data + load cached live data when pair changes
  useEffect(() => {
    if (!productA || !productB) return;
    setReviews((prev) => ({
      ...prev,
      [productA.id]: prev[productA.id] ?? fallbackUserReviews(productA),
      [productB.id]: prev[productB.id] ?? fallbackUserReviews(productB),
    }));
    let cancelled = false;
    (async () => {
      const cached = await getCachedReviews([productA.id, productB.id]);
      if (cancelled) return;
      if (Object.keys(cached).length > 0) {
        setReviews((prev) => ({ ...prev, ...cached }));
      }
      // Hybrid free tier: fire fetch-product-reviews only for products
      // with no cache row yet. Deduped per session inside the helper.
      const fetched = await refreshReviewsIfMissing([productA, productB], cached);
      if (cancelled || Object.keys(fetched).length === 0) return;
      setReviews((prev) => ({ ...prev, ...fetched }));
    })();
    return () => {
      cancelled = true;
    };
  }, [productA, productB]);

  const handleRefreshReviews = async (p: Product) => {
    setRefreshing((r) => ({ ...r, [p.id]: true }));
    toast.info(`Pulling fresh review data for ${p.brand} ${p.model}…`);
    const fresh = await refreshReviews(p, true);
    setRefreshing((r) => ({ ...r, [p.id]: false }));
    if (fresh) {
      setReviews((prev) => ({ ...prev, [p.id]: fresh }));
      toast.success(`Updated — ${fresh.reviewCount} reviews from ${fresh.sources.length} sources`);
    } else {
      toast.error("Couldn't refresh review data right now.");
    }
  };

  const handleCategoryChange = (cat: ProductCategory) => {
    setSelectedCategory(cat);
    setProductAId("");
    setProductBId("");
  };

  if (!productA || !productB || !computedA || !computedB) return null;

  // Helper: get displayed score for a rating key
  const getScore = (
    which: "A" | "B",
    key: "reliability" | "abuseTolerance" | "buildQuality" | "userReviews",
  ): number => {
    const product = which === "A" ? productA : productB;
    const computed = which === "A" ? computedA : computedB;
    if (key === "userReviews") return reviews[product.id]?.score ?? product.ratings.userReviews;
    return computed[key].score;
  };

  const ratingKeys: { key: "reliability" | "abuseTolerance" | "buildQuality" | "userReviews"; label: string; computed: boolean }[] = [
    { key: "reliability", label: "Reliability", computed: true },
    { key: "abuseTolerance", label: "Abuse Tolerance", computed: true },
    { key: "buildQuality", label: "Build Quality", computed: true },
    { key: "userReviews", label: "User Reviews", computed: false },
  ];

  const avgA = Math.round(
    ratingKeys.reduce((sum, r) => sum + getScore("A", r.key), 0) / ratingKeys.length,
  );
  const avgB = Math.round(
    ratingKeys.reduce((sum, r) => sum + getScore("B", r.key), 0) / ratingKeys.length,
  );
  const winner = avgA > avgB ? "A" : avgB > avgA ? "B" : "tie";

  const specKeys = [...new Set([...Object.keys(productA.specs), ...Object.keys(productB.specs)])];

  return (
    <TooltipProvider delayDuration={150}>
      <section className="py-14 sm:py-20 md:py-28">
        <div className="container px-4 sm:px-6">
          {/* Section header */}
          <div className="mb-8 sm:mb-10">
            <p className="section-label text-sm sm:text-base font-mono text-primary uppercase tracking-wider mb-2">Quick Compare</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-foreground">
              Pick Two. See Who Wins.
            </h2>
            <p className="text-muted-foreground mt-2 max-w-lg text-base sm:text-lg">
              Choose a category, pick any two products, and see real ratings side by side.
            </p>
            <RatingMethodologyCallout />
          </div>

          {/* Category selector */}
          <div className="mb-4 sm:mb-6">
            <div className="flex overflow-x-auto border border-border rounded-lg">
              {compareCategoryOptions.map((cat) => {
                const Icon = cat.icon;
                const count = allProducts.filter(p => p.category === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex min-w-[92px] sm:min-w-0 shrink-0 flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-base font-mono font-bold uppercase tracking-tight sm:tracking-wider transition-all border-r last:border-r-0 border-border ${
                      selectedCategory === cat.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                  >
                    <Icon className="w-3 h-3 sm:w-5 sm:h-5 shrink-0" />
                    <span className="truncate sm:hidden">{cat.mobileLabel}</span>
                    <span className="hidden sm:inline truncate">{cat.label}</span>
                    <span className={`text-[8px] sm:text-sm px-1 py-0.5 rounded-full shrink-0 ${
                      selectedCategory === cat.id
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Product selectors */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-end gap-2 sm:gap-4 mb-6 sm:mb-8">
            <ProductSearchCombobox
              selectedId={effectiveAId}
              onSelect={setProductAId}
              excludeId={effectiveBId}
              label="Product A"
              categoryFilter={selectedCategory}
            />
            <motion.button
              whileTap={{ scale: 0.9, rotate: 180 }}
              onClick={() => { setProductAId(effectiveBId); setProductBId(effectiveAId); }}
              className="mb-0.5 p-2.5 border border-border rounded-lg text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors bg-surface-sunken self-center justify-self-center"
              title="Swap products"
            >
              <ArrowLeftRight className="w-5 h-5" />
            </motion.button>
            <ProductSearchCombobox
              selectedId={effectiveBId}
              onSelect={setProductBId}
              excludeId={effectiveAId}
              label="Product B"
              categoryFilter={selectedCategory}
            />
          </div>

          {/* Comparison result */}
          <motion.div
            key={`${effectiveAId}-${effectiveBId}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="border border-border rounded-xl bg-card shadow-elevated overflow-hidden"
          >
            {/* Header with product names & overall */}
            <div className="grid grid-cols-[1fr_auto_1fr] border-b border-border">
              <div className={`p-2 sm:p-4 md:p-5 overflow-hidden ${winner === "A" ? "bg-primary/5" : ""}`}>
                <div className="flex items-start gap-1.5 sm:gap-3">
                  <ProductThumbnail product={productA} size="sm" className="mt-0.5 shrink-0 hidden sm:block" />
                  <div className="min-w-0 overflow-hidden">
                    <p className="text-xs sm:text-base font-mono font-semibold text-muted-foreground uppercase mb-0.5 truncate">{productA.brand}</p>
                    <h3 className="text-base sm:text-xl md:text-2xl font-display font-bold text-foreground leading-tight truncate">{productA.model}</h3>
                    <span className="text-lg sm:text-2xl font-mono font-bold text-foreground">${productA.price}</span>
                    {winner === "A" && (
                      <div className="flex items-center gap-0.5 sm:gap-1 mt-1 sm:mt-1.5">
                        <Trophy className="w-3 h-3 sm:w-5 sm:h-5 text-primary shrink-0" />
                        <span className="text-xs sm:text-base font-mono text-primary font-bold uppercase">Winner</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center p-1 sm:p-4 border-x border-border bg-surface-sunken/30 min-w-0">
                <GitCompare className="w-4 h-4 sm:w-6 sm:h-6 text-muted-foreground mb-0.5 sm:mb-1" />
                <span className="text-sm sm:text-base font-mono font-bold text-muted-foreground uppercase">VS</span>
                <div className="flex items-center gap-1 sm:gap-2 mt-1 sm:mt-2">
                  <span className={`text-sm sm:text-lg font-mono font-bold ${winner === "A" ? "text-primary" : "text-foreground"}`}>{avgA}</span>
                  <span className="text-sm sm:text-base text-muted-foreground">—</span>
                  <span className={`text-sm sm:text-lg font-mono font-bold ${winner === "B" ? "text-accent" : "text-foreground"}`}>{avgB}</span>
                </div>
              </div>

              <div className={`p-2 sm:p-4 md:p-5 overflow-hidden ${winner === "B" ? "bg-accent/5" : ""}`}>
                <div className="flex items-start gap-1.5 sm:gap-3 justify-end">
                  <div className="min-w-0 overflow-hidden text-right">
                    <p className="text-xs sm:text-base font-mono font-semibold text-muted-foreground uppercase mb-0.5 truncate">{productB.brand}</p>
                    <h3 className="text-base sm:text-xl md:text-2xl font-display font-bold text-foreground leading-tight truncate">{productB.model}</h3>
                    <span className="text-lg sm:text-2xl font-mono font-bold text-foreground">${productB.price}</span>
                    {winner === "B" && (
                      <div className="flex items-center gap-0.5 sm:gap-1 mt-1 sm:mt-1.5 justify-end">
                        <Trophy className="w-3 h-3 sm:w-5 sm:h-5 text-accent shrink-0" />
                        <span className="text-xs sm:text-base font-mono text-accent font-bold uppercase">Winner</span>
                      </div>
                    )}
                  </div>
                  <ProductThumbnail product={productB} size="sm" className="mt-0.5 shrink-0 hidden sm:block" />
                </div>
              </div>
            </div>

            {/* Rating bars */}
            <div className="p-4 md:p-6 space-y-5">
              {(() => {
                let aWinsCount = 0, bWinsCount = 0, ties = 0;
                ratingKeys.forEach(r => {
                  const a = getScore("A", r.key), b = getScore("B", r.key);
                  if (a > b) aWinsCount++; else if (b > a) bWinsCount++; else ties++;
                });
                const total = ratingKeys.length;
                const leadName = aWinsCount > bWinsCount ? productA.model : bWinsCount > aWinsCount ? productB.model : null;
                const leadCount = Math.max(aWinsCount, bWinsCount);
                const leadColor = aWinsCount > bWinsCount ? "text-primary border-primary/40 bg-primary/10" : bWinsCount > aWinsCount ? "text-accent border-accent/40 bg-accent/10" : "text-muted-foreground border-border bg-surface-sunken";
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${leadColor}`}>
                    <Trophy className="w-4 h-4 shrink-0" />
                    <p className="text-xs sm:text-sm font-mono font-bold uppercase tracking-wider">
                      {leadName ? <><span className="truncate">{leadName}</span> wins {leadCount} of {total} categories</> : <>All {total} categories tied</>}
                      {leadName && ties > 0 && <span className="ml-1 text-muted-foreground normal-case font-semibold">· {ties} tied</span>}
                    </p>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-base font-mono text-primary uppercase tracking-wider font-bold">Ratings</p>
              </div>
              {ratingKeys.map((r) => {
                const valueA = getScore("A", r.key);
                const valueB = getScore("B", r.key);
                return (
                  <RatingBar
                    key={r.key}
                    label={r.label}
                    valueA={valueA}
                    valueB={valueB}
                  />
                );
              })}
            </div>

            {/* Specs comparison */}
            <div className="border-t border-border">
              <div className="p-4 md:p-6">
                <p className="text-base font-mono text-accent uppercase tracking-wider font-bold mb-4">Specifications</p>
                <div className="space-y-0">
                  {specKeys.map((key, i) => (
                    <div
                      key={key}
                      className={`grid grid-cols-3 py-2.5 text-sm sm:text-base font-mono ${
                        i % 2 === 0 ? "bg-surface-sunken/20" : ""
                      } ${i < specKeys.length - 1 ? "border-b border-border/30" : ""}`}
                    >
                      <span className="font-bold text-foreground px-2">{productA.specs[key] ?? "—"}</span>
                      <span className="text-center text-muted-foreground px-2">{key}</span>
                      <span className="text-right font-bold text-foreground px-2">{productB.specs[key] ?? "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA to full products page */}
          <div className="flex justify-center mt-8">
            <Link to="/products">
              <motion.button
                whileTap={{ scale: 0.97 }}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-mono font-bold text-sm sm:text-base uppercase tracking-wider rounded-lg shadow-md hover:brightness-110 transition-all"
              >
                Browse All Products
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
};

export default ComparisonSection;
